import React, { useState } from "react";

import {
  DownloadOutlined, HistoryOutlined, PlayCircleOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";

import { useAccessStatus } from "../../../../grace-period/hooks/useAccessStatus";
import { TEMPLATE_SIDES } from "../../../../Common/Template/TemplateContext";
import CoaPreviewModal from "../Preview/CoaPreviewModal";
import ReportHistory from "../ReportHistory/ReportHistory";
import { VALID_SPECS_SET } from "../ReportSpecs/helper";

import { downloadFileFromS3WithPresigned } from "../../../../../utils/helpers";
import { hasPermission, permissions } from "../../../../../utils/Permissions";

import loadingIcon from "../../../../../assets/images/product/refreshCircleBlack.png";

import "./ReportButtons.css";

export default function ReportButtons({
  item,
  // toggleShareModal,
}) {
  const [showPreview, setShowPreview] = useState(false);
  const [showReportHistory, setShowReportHistory] = useState(false);
  const { areResultsLocked } = useAccessStatus();

  /**
   *  Handles the download of files for a report.
   *  Checks if a valid file path exists for the report, then proceeds to download
   *  all files associated with the report. It keeps track of the total number of
   *  files and the number of files successfully downloaded, displaying a success
   *  or warning message accordingly.
   *  @param {none}
   *  @return {none}
   */
  const handleDownloadFile = () => {
    if (!item.path || Object.keys(item.path).length < 1) {
      toast.error("Unable to download, no valid file path for this report!");
      return;
    }

    const files = Object.entries(item.path);
    let totalFiles = 0;
    let filesDownloaded = 0;
    Promise.all(files.map(async (fileData) => {
      const fileTempSet = Object.values(fileData[1]);
      const fileTempArr = Array.from(fileTempSet);

      return Promise.all(fileTempArr.map(async (fileTemp) => {
        totalFiles += fileTemp.length;

        return Promise.all(fileTemp.map(async (filePath) => {
          const fileName = filePath.split("/").pop();
          const isPrivateS3Bucket = filePath.includes("COAs/");
          const s3Bucket = isPrivateS3Bucket ? "private" : "public";

          const success = await downloadFileFromS3WithPresigned([filePath], s3Bucket, [fileName]);
          if (success) {
            filesDownloaded++;
          } else {
            throw new Error("Failed to download files");
          }
        }));
      }));
    })).then(() => {
      if (filesDownloaded === totalFiles) {
        toast.success(`Successfully downloaded ${filesDownloaded}/${totalFiles} files.`);
      } else {
        toast.warning(`Downloaded ${filesDownloaded}/${totalFiles} files.`);
      }
    }).catch((error) => {
      toast.error(error.message);
    });
  };

  // Check if report_history exists and has items
  // const hasReportHistory = item.report_history && Array.isArray(item.report_history) && item.report_history.length > 0;

  // For "Test In Process" state: check if report_history is loaded (not null/undefined)
  // const isReportHistoryLoaded = item.report_history !== null && item.report_history !== undefined;

  // Hide the history action when partial_report_generated is false so users do not open an empty modal.
  const showPartialReportHistory = hasPermission(permissions.product.generatePartial)
    && Boolean(item.partial_report_generated);

  if (item.specs_flag === "0") {
    return (
      <>
        {showReportHistory && !areResultsLocked && (
          <ReportHistory
            sampleId={item.sample_id}
            sampleType={item.sample_type || "Product"}
            receivedDate={item.received_date}
            setShowReportHistory={setShowReportHistory}
            reportHistory={item.report_history}
            report={item}
          />
        )}
        <div className="ProductReportTableActions__InProcess">
          <div className="ProductReportTableActions__InProcess__Left">
            {/*
                hide the loading icon when the user has generate-partial, so it does not sit next to history.
            */}
            {!hasPermission(permissions.product.generatePartial) && (
              <img src={loadingIcon} alt="Loading" className="ProductReportTableActions__InProcess__LoadingIcon" />
            )}
            <span>
              Test In Process
            </span>
          </div>
          {showPartialReportHistory && !areResultsLocked && (
            <HistoryOutlined
              className="ProductReportTableActions__HistoryIcon"
              onClick={() => setShowReportHistory(true)}
              aria-label="Report history"
            />
          )}
        </div>
      </>
    );
  }

  const enableButtons = VALID_SPECS_SET.has(item.specs_flag.toString()) && item.path && Object.keys(item.path).length > 0
    && !areResultsLocked;

  return (
    <>
      {showPreview && (
        <CoaPreviewModal
          filePaths={item.path}
          report={item}
          handleCloseForPreview={() => setShowPreview(false)}
          side={TEMPLATE_SIDES.PRODUCT}
        />
      )}
      {showReportHistory && (
        <ReportHistory
          sampleId={item.sample_id}
          sampleType={item.sample_type || "Product"}
          receivedDate={item.received_date}
          setShowReportHistory={setShowReportHistory}
          reportHistory={item.report_history}
          report={item}
        />
      )}
      <div className="ProductReportTableActions">
        <PlayCircleOutlined
          className={`ProductReportTableActions__PreviewIcon${!enableButtons ? " ProductReportTableActions__PreviewIcon--disabled" : ""}`}
          onClick={enableButtons ? () => setShowPreview(true) : undefined}
          aria-label={enableButtons ? "Preview report" : "Preview unavailable"}
          aria-disabled={!enableButtons}
        />
        <DownloadOutlined
          className={`ProductReportTableActions__DownloadIcon${enableButtons ? "" : " ProductReportTableActions__DownloadIcon--disabled"}`}
          onClick={enableButtons ? handleDownloadFile : undefined}
          aria-label={enableButtons ? "Download report" : "Download unavailable"}
          aria-disabled={!enableButtons}
        />
        {showPartialReportHistory && !areResultsLocked && (
          <HistoryOutlined
            className="ProductReportTableActions__HistoryIcon"
            onClick={() => setShowReportHistory(true)}
            aria-label="Report history"
          />
        )}
        {/* <HistoryIcon
          className={`ProductReportTableActions__HistoryIcon${!hasReportHistory ? "--disabled" : ""}`}
          onClick={hasReportHistory ? () => setShowReportHistory(true) : null}
        /> */}
        {/* <ShareIcon
        className={`ProductReportTableActions__ShareIcon${enableButtons ? "" : "--disabled"}`}
        onClick={enableButtons ? () => toggleShareModal(i, true) : null}
      /> */}
      </div>
    </>

  );
}
